import * as turf from "@turf/turf";
import RBush from "rbush";
import knn from "./helpers";


export default class RouteMetrics {

  constructor(route) {
    this._route = route;
    this._linestring = turf.lineString(route.geometry.coordinates);

    this._segIndex = new RBush();
    this._cumLens = [0];
    this._speeds = route.legs[0].annotation.speed;

    this._coords = this._linestring.geometry.coordinates;
    let total = 0;

    this._congestionLinestrings = [];


    let congestions = route.legs[0].annotation.congestion_numeric;
    let cbuf = [];

    for (let i = 0; i < this._coords.length - 1; i++) {
      const A = this._coords[i], B = this._coords[i + 1];


      // Compile lens
      let len = this.distance(A, B);
      total += len;
      this._cumLens.push(total);

      this._segIndex.insert({
        minX: Math.min(A[0], B[0]),
        minY: Math.min(A[1], B[1]),
        maxX: Math.max(A[0], B[0]),
        maxY: Math.max(A[1], B[1]),
        i // segment index
      });


      // Compile congestion
      let congestion = congestions[i];

      if (congestion > 30) {

        if (!cbuf.length) {
          cbuf.push(A);
        }

        cbuf.push(B);

      } else if (cbuf.length) {
        this._congestionLinestrings.push(cbuf);
        cbuf = [];
      }
    }

    if (cbuf.length) {
      this._congestionLinestrings.push(cbuf);
    }

    this._routeLength = total;
  }


  get congestionGeometry() {

    return {
      type: "FeatureCollection",
      features: this._congestionLinestrings.map(l => ({
        type: "Feature",
        geometry: {
          type: "LineString",
          coordinates: l,
          properties: {

          }
        }
      }))
    }
  }


  get duration() {
    return this._route.duration;
  }

  get lengthKm() {
    return this._routeLength;
  }

  get legs() {
    return this._route.legs;
  }


  get geometry() {
    return this._route.geometry;
  }

  getBearing(idx) {
    if (this._coords.length < 2 || idx >= this._coords.length - 2) return 0;
    const A = this._coords[idx], B = this._coords[idx + 1];
    return turf.bearing(turf.point(A), turf.point(B));
  }

  speed(d) {
    d = Math.max(0, Math.min(d, this._routeLength));
    const k = this.binarySearch(d);

    if (k < 0 || k > this._speeds.length - 1) {
      return 5;
    } else {
      return Math.max(5, this._speeds[k]);
    }
  }

  along(d) {
    d = Math.max(0, Math.min(d, this._routeLength));

    // find segment index k
    const k = this.binarySearch(d);

    // distance from segment start
    const segStart = this._cumLens[k];
    const segLen = this._cumLens[k + 1] - segStart;
    const t = segLen > 0 ? (d - segStart) / segLen : 0;  // guard zero-length segment

    // interpolate between coords[k] and coords[k+1]
    const a0 = this.interpolate(this._coords[k], this._coords[k + 1], t);

    return turf.point(a0);
  }

  interpolate(A, B, t) {
    return [
      A[0] + (B[0] - A[0]) * t,
      A[1] + (B[1] - A[1]) * t
    ];
  }

  binarySearch(d) {
    let lo = 0;
    let hi = this._cumLens.length - 1;
    while (lo <= hi) {
      const mid = Math.floor((lo + hi) / 2);
      if (this._cumLens[mid] <= d) {
        lo = mid + 1;
      } else {
        hi = mid - 1;
      }
    }
    return Math.max(0, Math.min(hi, this._cumLens.length - 2));
  }



  nearestPointOnLine(pt) {
    const P = pt.geometry.coordinates;

    const closestSegs = knn(this._segIndex, P[0], P[1], 5);
    let closest = Infinity;
    let closestPt = null;
    let closestIndex = 0;

    closestSegs.forEach(s => {
      const A = this._coords[s.i], B = this._coords[s.i + 1];
      const proj = this.projectPointOnSegment(P, A, B);
      const dist = this.distance(proj, P);
      if (dist < closest) {
        closest = dist;
        closestPt = proj;
        closestIndex = s.i;
      }
    });

    const cumDist = this.distance(closestPt, this._coords[closestIndex]) + this._cumLens[closestIndex];

    const pts = turf.point(closestPt, {
      location: cumDist,
      dist: closest,
      index: closestIndex
    });

    return pts;
  }




  projectPointOnSegment(P, A, B) {
    const x0 = P[0], y0 = P[1];
    const x1 = A[0], y1 = A[1];
    const x2 = B[0], y2 = B[1];

    const dx = x2 - x1;
    const dy = y2 - y1;
    const len2 = dx * dx + dy * dy;

    // If A and B are the same point, return A
    if (len2 === 0) {
      return [x1, y1];
    }

    // Project point onto the infinite line, computing parameter t
    let t = ((x0 - x1) * dx + (y0 - y1) * dy) / len2;

    // Clamp t to [0,1] to restrict to segment
    t = Math.max(0, Math.min(1, t));

    // Compute the closest point
    return [
      x1 + t * dx,
      y1 + t * dy
    ];
  }


  // Haversine distance km
  distance(A, B) {
    const dLat = this.toRad(B[1] - A[1]);
    const dLon = this.toRad(B[0] - A[0]);
    const lat1 = this.toRad(A[1]);
    const lat2 = this.toRad(B[1]);

    const a = Math.pow(Math.sin(dLat / 2), 2) + Math.pow(Math.sin(dLon / 2), 2) * Math.cos(lat1) * Math.cos(lat2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

    return 6373 * c; // Radius of earth in km
  }

  toRad(degree) {
    return degree * Math.PI / 180;
  }

}